import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Edit, Upload, ArrowRight, CheckCircle2, Sparkles, ClipboardPaste, Zap, Link2, Eye, FileDown, Layers, GripVertical, Save, Briefcase, Clock } from "lucide-react";
import { ResumeAnalysis } from "@/types/resume";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

interface InputModeSelectorProps {
  onSelectMode: (mode: "form" | "paste" | "tailor") => void;
  onImportResume: (file: File) => void;
  onImportText?: (text: string) => void;
  isAnalyzing?: boolean;
  analysis?: ResumeAnalysis | null;
}

const highlights = [
  { icon: Clock, text: "Build in under 2 minutes" },
  { icon: Link2, text: "Save links for quick access" },
  { icon: Eye, text: "Toggle sections on/off" },
  { icon: FileDown, text: "Auto-named downloads" },
  { icon: GripVertical, text: "Drag to reorder sections" },
  { icon: Layers, text: "Switch between templates" },
  { icon: Save, text: "Save multiple resume versions" },
  { icon: Briefcase, text: "Track your applications" },
];

export const InputModeSelector = ({
  onSelectMode,
  onImportResume,
  onImportText,
  isAnalyzing = false,
  analysis = null,
}: InputModeSelectorProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pastedText, setPastedText] = useState("");
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_FILE_SIZE) {
      alert("File is too large. Please upload a file under 10 MB.");
      return;
    }
    onImportResume(file);
    setShowImportOptions(false);
  };

  const handlePasteSubmit = () => {
    if (pastedText.trim().length < 50) return;
    if (onImportText) {
      onImportText(pastedText);
    }
    setShowPasteDialog(false);
    setPastedText("");
  };

  return (
    <div className="w-full">
      {/* Hero banner */}
      <div className="relative overflow-hidden bg-primary/5 border-b border-border/50">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[80%] rounded-full bg-primary/8 blur-[100px]" />
        </div>
        <div className="w-full px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm font-semibold text-primary mb-6"
            >
              <Zap className="w-4 h-4" />
              Speed up your job applications
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4"
            >
              Build, Customize, Apply.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-muted-foreground text-lg max-w-2xl mx-auto mb-6"
            >
              Choose how you want to start. Your resume will be ATS-optimized and ready to download in minutes.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="text-sm text-muted-foreground"
            >
              Or go to your{" "}
              <button
                onClick={() => (window.location.href = "/dashboard")}
                className="text-primary hover:underline font-medium"
              >
                Applications Dashboard
              </button>{" "}
              to track your job applications
            </motion.p>
          </div>
        </div>
      </div>

      {/* Action cards */}
      <div className="w-full px-6 sm:px-10 lg:px-16 py-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Start Fresh */}
            <motion.button
              data-tour="start-fresh"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              onClick={() => onSelectMode("form")}
              className="group text-left w-full rounded-2xl border-2 border-border bg-card p-8 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                <Edit className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2 text-foreground">
                Start Fresh
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                Build your resume from scratch with our structured form and live preview. Great for first-timers.
              </p>
              <span className="inline-flex items-center text-sm font-medium text-primary group-hover:gap-2 transition-all">
                Get Started <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </motion.button>

            {/* Import Resume */}
            <motion.div
              data-tour="import-resume"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl border-2 border-border bg-card p-8 hover:border-accent/50 hover:shadow-xl hover:shadow-accent/5 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-5">
                <Upload className="w-7 h-7 text-accent" />
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2 text-foreground">
                Import Resume
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                Already have a resume? or unformatted content? Upload a file or paste the text. We will parse and structure it for you instantly.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                onChange={handleFileChange}
                className="hidden"
              />

              {isAnalyzing ? (
                <Button variant="outline" className="w-full" disabled>
                  <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin mr-2" />
                  Analyzing...
                </Button>
              ) : showImportOptions ? (
                <div className="space-y-2">
                  <Button variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setShowPasteDialog(true); setShowImportOptions(false); }}
                  >
                    <ClipboardPaste className="w-4 h-4 mr-2" />
                    Paste Text
                  </Button>
                  <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => setShowImportOptions(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button variant="outline" className="w-full" onClick={() => setShowImportOptions(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Resume
                </Button>
              )}
            </motion.div>

            {/* Tailor for Job */}
            <motion.button
              data-tour="tailor-job"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              onClick={() => onSelectMode("tailor")}
              className="group text-left w-full rounded-2xl border-2 border-border bg-card p-8 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute top-3 right-3">
                <span className="text-[10px] font-semibold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                  NEW
                </span>
              </div>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-all duration-300">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2 text-foreground">
                Tailor for Job
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                Paste a job description + upload your resume. AI creates a perfectly tailored version for that role.
              </p>
              <span className="inline-flex items-center text-sm font-medium text-primary group-hover:gap-2 transition-all">
                Build for Job <Sparkles className="w-4 h-4 ml-1" />
              </span>
            </motion.button>
          </div>

          {/* Template previews */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-10"
          >
            <h3 className="font-heading font-semibold text-lg text-foreground mb-1 text-center">
              Professional Resume Templates
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              Choose from two clean, ATS-friendly designs when building your resume.
            </p>
            <div className="grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {[
                { src: "/templates/resume-modern.jpg", label: "Modern", desc: "Clean layout with accent color sidebar" },
                { src: "/templates/resume-harvard.jpg", label: "Harvard", desc: "Classic single-column academic style" },
              ].map(({ src, label, desc }) => (
                <div key={label} className="rounded-2xl border-2 border-border bg-card overflow-hidden hover:border-primary/40 hover:shadow-xl transition-all duration-300 group">
                  <div
                    className="aspect-[3/4] overflow-hidden bg-muted/30 cursor-zoom-in relative"
                    onClick={() => setLightboxSrc(src)}
                  >
                    <img
                      src={src}
                      alt={`${label} Template`}
                      className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-300"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/20">
                      <span className="bg-black/60 text-white text-xs font-medium px-3 py-1.5 rounded-full">Click to expand</span>
                    </div>
                  </div>
                  <div className="p-4 text-center">
                    <span className="font-semibold text-foreground text-sm">{label}</span>
                    <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Lightbox */}
          <AnimatePresence>
            {lightboxSrc && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
                onClick={() => setLightboxSrc(null)}
              >
                <button
                  className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
                  onClick={() => setLightboxSrc(null)}
                >
                  <X className="w-5 h-5" />
                </button>
                <motion.img
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.85, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  src={lightboxSrc}
                  alt="Template preview"
                  className="max-h-[92vh] max-w-[95vw] object-contain rounded-xl shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Feature highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl bg-primary/5 border border-primary/10 p-8"
          >
            <h3 className="font-heading font-semibold text-lg text-foreground mb-2 text-center">
              Why Resume Flow AI?
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-6">
              It is not just another AI resume builder. It is a workflow tool that makes applying to jobs faster.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {highlights.map((h, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border/50">
                  <h.icon className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm text-foreground">{h.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Paste Text Dialog */}
      <Dialog open={showPasteDialog} onOpenChange={setShowPasteDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="w-5 h-5" />
              Paste Your Resume Text
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Copy and paste your entire resume content below. We will parse and format it for you.
            </p>
            <Textarea
              placeholder="Paste your resume content here...

Example:
John Doe
Software Engineer
john.doe@email.com | (555) 123-4567

EXPERIENCE
Company Name | Jan 2022 - Present
- Developed web applications using React and TypeScript
- Led team of 5 developers on major product launches
..."
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              className="min-h-[300px] font-mono text-sm"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {pastedText.length} characters{" "}
                {pastedText.length < 50 && pastedText.length > 0 && "(minimum 50)"}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setShowPasteDialog(false); setPastedText(""); }}>
                  Cancel
                </Button>
                <Button onClick={handlePasteSubmit} disabled={pastedText.trim().length < 50 || isAnalyzing}>
                  {isAnalyzing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Import & Analyze
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analysis Results */}
      {analysis && (
        <div className="w-full px-6 sm:px-10 lg:px-16 pb-10">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border-2 border-primary/50 bg-card p-6"
            >
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                Resume Imported Successfully
              </h3>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Initial ATS Score</h4>
                  <div className="text-3xl font-bold text-primary">
                    {analysis.score > 0 ? `${analysis.score}%` : "Analyzing..."}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Your resume is structured and ready to review
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    {(analysis.weaknesses || []).length > 0 ? "Areas to Optimize" : "Resume Status"}
                  </h4>
                  {(analysis.weaknesses || []).length > 0 ? (
                    <ul className="space-y-1">
                      {(analysis.weaknesses || []).slice(0, 3).map((weakness, idx) => (
                        <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                          <span className="text-warning mt-0.5">&#8226;</span>
                          {weakness}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Your resume has been parsed. Continue to review and optimize.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <Button variant="default" onClick={() => onSelectMode("form")} className="flex-1">
                  <Eye className="w-4 h-4 mr-2" />
                  View My Resume
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground pb-8">
        Your data is processed securely and never stored without your permission.
      </p>
    </div>
  );
};
